import { useState } from "react";
import { C, API_BASE, getFontFamily } from "../constants/colors";
import { authedFetch } from "../firebase";
import { LANG } from "../constants/lang";
import { findConceptDetails } from "../constants/curriculum";
import { LangToggle } from "./ui";

export function TestApp({ profile, lang, setLang, onHome, topic, subject, curriculumData }) {
  const ff = lang==="hi"?"'Noto Sans Devanagari','Poppins',sans-serif":"'Poppins',sans-serif";
  const sc = subject && {
    Maths:     {bg:C.amberDark,accent:C.amberDark,light:C.amberSoft,text:C.amberDeep},
    Science:   {bg:C.primaryMid,accent:C.primaryMid,light:C.primarySoft,text:C.primaryDeep},
    Geography: {bg:C.greenMid,accent:C.greenMid,light:C.greenSoft,text:"#27500A"},
    Economics: {bg:C.purpleMid,accent:C.purpleMid,light:C.purpleSoft,text:"#3C3489"},
  }[subject] || {bg:C.primaryMid,accent:C.primaryMid,light:C.primarySoft,text:C.primaryDeep};

  // Find concept details from curriculum for richer question generation
  const conceptInfo = curriculumData ? findConceptDetails(curriculumData, profile?.studentClass||10, topic) : null;

  const [phase, setPhase] = useState("pick");
  const [count, setCount] = useState(5);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [scores, setScores] = useState([]);

  const loadQuestions = async (n) => {
    setPhase("loading");
    try {
      const res = await authedFetch(`${API_BASE}/api/test-questions`, {
        method:"POST",
        body: JSON.stringify({
          topic, subject,
          studentClass:profile?.studentClass||10,
          board:profile?.board||"CBSE",
          count:n, lang,
          // Pass concept details for smarter generation
          conceptDefinition: conceptInfo?.definition || "",
          conceptFormulas: conceptInfo?.formulas || [],
          chapterName: conceptInfo?.chapter || "",
        })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text||"[]";
      const cleaned = text.replace(/```json|```/g,"").trim();
      const qs = JSON.parse(cleaned);
      setQuestions(qs);
      setPhase("quiz");
    } catch(e) {
      console.error(e);
      setPhase("pick");
    }
  };

  const handleSelect = (idx) => {
    if(answered) return;
    setSelected(idx);
    setAnswered(true);
    const q = questions[current];
    setScores(s=>[...s,{correct:idx===q.correct,question:q.question,selectedIdx:idx,correctIdx:q.correct,explanation:q.explanation}]);
  };

  const handleNext = () => {
    if(current < questions.length - 1) {
      setCurrent(c=>c+1);
      setSelected(null);
      setAnswered(false);
    } else {
      setPhase("insights");
    }
  };

  const handleSkip = () => {
    setScores(s => [...s, { correct: false, question: questions[current].question, skipped: true }]);
    if(current < questions.length - 1) {
      setCurrent(c=>c+1);
      setSelected(null);
      setAnswered(false);
    } else {
      setPhase("insights");
    }
  };

  const correctCount = scores.filter(s=>s.correct).length;

  // ── Phase: Pick count ──────────────────────────────────────────────────────
  if(phase==="pick") return (
    <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E8F2F8",fontFamily:ff}}>
      <div style={{background:sc.bg,padding:"20px 20px 40px"}}>
        <button onClick={onHome} style={{background:"rgba(255,255,255,0.2)",border:"none",cursor:"pointer",padding:"6px 12px",borderRadius:20,color:"white",fontSize:13,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6,fontFamily:ff}}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          {lang==="hi"?"वापस":"Back"}
        </button>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.6)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:4}}>TEST ME · {subject?.toUpperCase()}</div>
        <div style={{fontSize:20,fontWeight:600,color:"white",lineHeight:1.2}}>{topic}</div>
      </div>
      <div style={{padding:"28px 20px",maxWidth:400,margin:"0 auto",marginTop:-16}}>
        <div style={{background:"white",borderRadius:20,padding:"28px 24px",boxShadow:"0 4px 20px rgba(0,0,0,0.08)"}}>
          <div style={{fontSize:17,fontWeight:700,color:C.text,marginBottom:6}}>{lang==="hi"?"कितने सवाल?":"How many questions?"}</div>
          <div style={{fontSize:13,color:C.textMute,marginBottom:24}}>{lang==="hi"?"MCQ · सीधा जवाब · स्कोर के साथ":"MCQ · Direct answer · With score"}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:24}}>
            {[5,10].map(n=>(
              <button key={n} onClick={()=>setCount(n)} style={{
                padding:"20px",borderRadius:16,border:`2px solid ${count===n?sc.accent:C.border}`,
                background:count===n?sc.light:"white",cursor:"pointer",fontFamily:ff,
                fontSize:26,fontWeight:600,color:count===n?sc.accent:C.textMid,
                transition:"all .15s",
              }}>{n}<div style={{fontSize:12,fontWeight:500,marginTop:4}}>{lang==="hi"?"सवाल":"questions"}</div></button>
            ))}
          </div>
          <button onClick={()=>loadQuestions(count)} style={{
            width:"100%",padding:"15px",background:sc.bg,color:"white",border:"none",
            borderRadius:14,fontSize:16,fontWeight:700,cursor:"pointer",fontFamily:ff,
            boxShadow:`0 4px 16px ${sc.accent}33`,
          }}>{lang==="hi"?"शुरू करें →":"Start test →"}</button>
        </div>
      </div>
    </div>
  );

  // ── Phase: Loading ─────────────────────────────────────────────────────────
  if(phase==="loading") return (
    <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E8F2F8",fontFamily:ff,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
      <div style={{display:"flex",gap:6}}>{[0,1,2].map(i=><div key={i} style={{width:10,height:10,borderRadius:"50%",background:sc.accent,animation:`td 1.2s ease-in-out ${i*0.2}s infinite`}}/>)}</div>
      <div style={{fontSize:14,color:C.textMute}}>{lang==="hi"?"सवाल तैयार हो रहे हैं...":"Preparing your questions..."}</div>
    </div>
  );

  // ── Phase: Quiz ────────────────────────────────────────────────────────────
  if(phase==="quiz" && questions.length>0) {
    const q = questions[current];
    return (
      <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E8F2F8",fontFamily:ff}}>
        {/* Header */}
        <div style={{background:sc.bg,padding:"16px 20px 28px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <button onClick={onHome} style={{background:"rgba(255,255,255,0.2)",border:"none",cursor:"pointer",padding:"5px 10px",borderRadius:16,color:"white",fontSize:13,fontWeight:600,fontFamily:ff}}>✕</button>
            <span style={{fontSize:13,fontWeight:700,color:"rgba(255,255,255,0.8)"}}>{lang==="hi"?`${current+1} / ${questions.length}`:`${current+1} of ${questions.length}`}</span>
            <span style={{fontSize:13,fontWeight:700,color:"rgba(255,255,255,0.8)"}}>{lang==="hi"?`${scores.filter(s=>s.correct).length} सही`:`${scores.filter(s=>s.correct).length} correct`}</span>
          </div>
          {/* Progress bar */}
          <div style={{height:4,background:"rgba(255,255,255,0.2)",borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",background:"white",borderRadius:4,width:`${((current)/questions.length)*100}%`,transition:"width .4s ease"}}/>
          </div>
        </div>

        <div style={{padding:"20px",maxWidth:520,margin:"0 auto",marginTop:-8}}>
          {/* Question */}
          <div style={{background:"white",borderRadius:20,padding:"24px",marginBottom:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:11,fontWeight:700,color:sc.accent,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>QUESTION {current+1}</div>
            <div style={{fontSize:16,fontWeight:600,color:C.text,lineHeight:1.6}}>{q.question}</div>
          </div>

          {/* Options */}
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
            {q.options.map((opt,i)=>{
              let bg = "white", border = C.border, color = C.text;
              if(answered) {
                if(i===q.correct) { bg="#f0fdf4"; border="#16a34a"; color="#15803d"; }
                else if(i===selected && i!==q.correct) { bg="#fef2f2"; border="#ef4444"; color="#dc2626"; }
              } else if(selected===i) { bg=sc.light; border=sc.accent; }
              return (
                <button key={i} onClick={()=>handleSelect(i)} style={{
                  background:bg,border:`1.5px solid ${border}`,borderRadius:14,
                  padding:"14px 18px",textAlign:"left",cursor:answered?"default":"pointer",
                  fontFamily:ff,fontSize:14,fontWeight:600,color,
                  display:"flex",alignItems:"center",gap:12,transition:"all .15s",
                }}>
                  <span style={{width:28,height:28,borderRadius:8,background:answered&&i===q.correct?"#16a34a":answered&&i===selected&&i!==q.correct?"#ef4444":border===C.border?"#EDF4F8":sc.accent,color:answered&&(i===q.correct||(i===selected&&i!==q.correct))?"white":answered?"#94a3b8":"white",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,flexShrink:0,transition:"all .15s"}}>
                    {answered && i===q.correct ? "✓" : answered && i===selected && i!==q.correct ? "✗" : ["A","B","C","D"][i]}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>

          {/* Skip button — only when not answered */}
          {!answered && (
            <button onClick={handleSkip} style={{
              width: "100%", padding: "12px", background: "none", border: "none",
              fontSize: 13, color: "#94a3b8", cursor: "pointer", fontFamily: ff,
            }}>
              {lang === "hi" ? "बाद में करूँगा →" : "Skip for now →"}
            </button>
          )}

          {/* Explanation + Next */}
          {answered && (
            <div style={{animation:"fadeUp .3s ease"}}>
              <div style={{background:selected===q.correct?"#f0fdf4":"#fef2f2",border:`1px solid ${selected===q.correct?"#bbf7d0":"#fecaca"}`,borderRadius:14,padding:"14px 16px",marginBottom:14,fontSize:13,color:selected===q.correct?"#15803d":"#dc2626",lineHeight:1.6}}>
                {selected===q.correct?"✓ ":"✗ "}{q.explanation}
              </div>
              <button onClick={handleNext} style={{
                width:"100%",padding:"14px",background:sc.bg,color:"white",border:"none",
                borderRadius:14,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:ff,
                boxShadow:`0 4px 16px ${sc.accent}33`,
              }}>
                {current < questions.length-1 ? (lang==="hi"?"अगला सवाल →":"Next question →") : (lang==="hi"?"नतीजे देखें →":"See results →")}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Phase: Insights ────────────────────────────────────────────────────────
  if(phase==="insights") {
    const skippedCount = scores.filter(s=>s.skipped).length;
    const answeredCount = questions.length - skippedCount;
    const pct = answeredCount > 0 ? Math.round((correctCount/answeredCount)*100) : 0;
    const grade = pct>=80?"🏆":pct>=60?"✓":"📚";
    const msg = pct>=80?(lang==="hi"?"शानदार! आपने इस टॉपिक को अच्छे से समझा है।":"Excellent! You have a strong grip on this topic.")
      :pct>=60?(lang==="hi"?"अच्छा! थोड़ी और प्रैक्टिस से पक्का हो जाएगा।":"Good effort! A bit more practice will solidify this.")
      :(lang==="hi"?"इस टॉपिक को पहले समझाएं, फिर टेस्ट दें।":"Try reviewing this topic first, then test again.");

    return (
      <div style={{minHeight:"calc(100vh / 0.92)", zoom: 0.92,background:"#E8F2F8",fontFamily:ff}}>
        <div style={{background:sc.bg,padding:"20px 20px 40px",textAlign:"center"}}>
          <div style={{fontSize:52,marginBottom:8}}>{grade}</div>
          <div style={{fontSize:26,fontWeight:600,color:"white",letterSpacing:"-0.03em",marginBottom:4}}>{correctCount}/{answeredCount}</div>
          <div style={{fontSize:15,color:"rgba(255,255,255,0.7)"}}>{topic}{skippedCount > 0 ? ` · ${skippedCount} skipped` : ""}</div>
        </div>

        <div style={{padding:"20px",maxWidth:480,margin:"0 auto",marginTop:-16}}>
          {/* Score card */}
          <div style={{background:"white",borderRadius:20,padding:"24px",marginBottom:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:14,color:C.textMute,marginBottom:16}}>{msg}</div>
            {/* Score bar */}
            <div style={{height:10,background:"#E8F2F8",borderRadius:10,overflow:"hidden",marginBottom:8}}>
              <div style={{height:"100%",background:pct>=80?"#16a34a":pct>=60?sc.accent:"#f59e0b",borderRadius:10,width:`${pct}%`,transition:"width 1s ease"}}/>
            </div>
            <div style={{fontSize:13,color:C.textMute}}>{pct}% correct</div>
          </div>

          {/* Per-question breakdown */}
          <div style={{background:"white",borderRadius:20,padding:"20px",marginBottom:16,boxShadow:"0 2px 12px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:12,fontWeight:600,color:C.textMute,letterSpacing:"0.03em",marginBottom:14}}>BREAKDOWN</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {scores.map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:i<scores.length-1?`1px solid ${C.border}`:"none"}}>
                  <span style={{width:24,height:24,borderRadius:6,background:s.skipped?"#EDF4F8":s.correct?"#f0fdf4":"#fef2f2",color:s.skipped?"#94a3b8":s.correct?"#16a34a":"#ef4444",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,flexShrink:0,marginTop:1}}>{s.skipped?"–":s.correct?"✓":"✗"}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,color:s.skipped?"#94a3b8":C.text,lineHeight:1.4,marginBottom:3}}>{s.question}{s.skipped?" (skipped)":""}</div>
                    {!s.correct&&!s.skipped&&<div style={{fontSize:12,color:"#16a34a"}}>✓ {questions[i]?.options[s.correctIdx]}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <button onClick={()=>{setCurrent(0);setSelected(null);setAnswered(false);setScores([]);loadQuestions(count);}} style={{width:"100%",padding:"14px",background:sc.bg,color:"white",border:"none",borderRadius:14,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:ff,boxShadow:`0 4px 16px ${sc.accent}33`,marginBottom:10}}>
            {lang==="hi"?"फिर से टेस्ट दें":"Try again"}
          </button>
          <button onClick={onHome} style={{width:"100%",padding:"12px",background:"none",color:C.textMute,border:"none",fontSize:14,cursor:"pointer",fontFamily:ff}}>
            {lang==="hi"?"होम पर जाएं":"Back to home"}
          </button>
        </div>
      </div>
    );
  }
  return null;
}


// ── Mode Selection Page (Direction D: Warm Confident) ─────────────────────────
